import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import {
  handleAddFAQ,
  handleAddQuestion,
  handleDeleteFAQ,
  handleDeleteQuestion,
  handleFetchFAQList,
  handleFetchQuestions,
  handleFetchSingleFAQ,
  handleSortQuestions,
  handleUpdateFAQ,
  handleUpdateQuestion,
} from './faq.helpers';
import {
  createAPIObjectForCreateFAQ,
  createAPIObjectForUpdateFAQ,
  createFAQList,
  createOnEditObject,
  getQuestionIds,
  createQuestionObject,
  getFilteredQuestionList,
  getEmptyQuestionObject,
  getToggleStatusNotif,
} from './faq.adapter';
import { faqTypes } from './faq.types';
import {
  setFAQList,
  setOnEditFAQ,
  setPageAndPageSize,
  setQuestionsList,
  setUpdatedFAQ,
  setQuestionData,
  deleteFAQById,
} from './faq.actions';
import { toast } from 'react-toastify';
import { getNotifMsg, handleErrors } from 'utils';

const mapState = ({ faqData, appData }) => ({
  faqList: faqData.faq.data,
  channelId: faqData.faq.channelId,
  page: faqData.faq.page,
  pageSize: faqData.faq.pageSize,
  questionsList: faqData.onEditFAQ.questions,
  locale: appData.locale,
});

// FAQ
export function* fetchFAQList({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const { page, pageSize } = yield select(mapState);
    const { channelId, requestedPage } = payload;
    const finalPage = requestedPage ? requestedPage : page;
    yield put(setPageAndPageSize({ channelId }));
    const response = yield handleFetchFAQList(
      finalPage,
      pageSize,
      channelId,
      locale
    );
    if (response.status === 'success') {
      const adaptedFAQList = createFAQList(response.data);
      yield put(setFAQList(adaptedFAQList));

      // add page
      yield put(
        setPageAndPageSize({
          total: response.data.total,
          channelId,
          page: 2,
        })
      );
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* fetchMoreFAQList({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const { faqList: prevFAQList } = yield select(mapState);
    const { pageSize, page, channelId } = payload;

    yield put(setPageAndPageSize({ loadingMore: true }));
    const response = yield handleFetchFAQList(
      page,
      pageSize,
      channelId,
      locale
    );
    if (response.status === 'success') {
      const adaptedFAQList = createFAQList(response.data);
      yield put(setFAQList([...prevFAQList, ...adaptedFAQList]));
      yield put(
        setPageAndPageSize({
          page: page + 1,
          pageSize,
          total: response.data.total,
        })
      );
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield put(setPageAndPageSize({ loadingMore: false }));
  }
}

export function* updateFAQEnable({ payload }) {
  const { locale } = yield select(mapState);

  const STATUS_TEXT_LOCALE = {
    fa: {
      enabled: 'فعال',
      disabled: 'غیرفعال',
    },
    en: {
      enabled: 'enabled',
      disabled: 'disabled',
    },
  };

  try {
    const { faqId, isEnabled } = payload;
    const textStatus = isEnabled
      ? STATUS_TEXT_LOCALE[locale].enabled
      : STATUS_TEXT_LOCALE[locale].disabled;
    const response = yield handleUpdateFAQ(
      faqId,
      { disabled: !isEnabled },
      locale
    );
    if (response.status === 'success') {
      toast.success(getToggleStatusNotif(textStatus, locale));
    }
    yield put(setUpdatedFAQ(faqId, { isEnabled }));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* fetchSingleFAQ({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const faqId = payload;
    const responseData = yield handleFetchSingleFAQ(faqId, locale);
    yield put(setOnEditFAQ({ ...createOnEditObject(responseData.data) }));
    return responseData.data;
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield put(setOnEditFAQ({ loading: false }));
  }
}

export function* addNewFAQ({ payload }) {
  const { faqData, setIsSubmittingFAQForm, history, prevUrl } = payload;
  const newFAQData = createAPIObjectForCreateFAQ(faqData);
  const { locale } = yield select(mapState);
  try {
    const response = yield handleAddFAQ(newFAQData, locale);

    // create a default empty question after adding new faq
    const emptyQuestionObject = getEmptyQuestionObject(response.data.id);

    if (response.status === 'success') {
      yield put(
        setOnEditFAQ({
          ...createOnEditObject(response.data),
          questions: [emptyQuestionObject],
        })
      );

      yield fetchFAQList({
        payload: { channelId: payload.faqData.channelId, requestedPage: 1 },
      });

      history.replace(`${prevUrl}/${response.data.id}`);
      toast.success(getNotifMsg('FAQ_SUCCESSFUL_ADDED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setIsSubmittingFAQForm(false);
  }
}

export function* updateFAQ({ payload }) {
  const { setIsSubmittingFAQForm, faqData } = payload;
  const { locale } = yield select(mapState);
  try {
    const { faqId, ...data } = createAPIObjectForUpdateFAQ(faqData);
    const responseData = yield handleUpdateFAQ(faqId, data, locale);
    const { faqList } = yield select(mapState);

    if (responseData.status === 'success') {
      const newFAQList = faqList.map((item) => {
        if (item.id === faqId) {
          return { ...item, ...data };
        }
        return item;
      });

      yield put(setFAQList(newFAQList));
      yield put(setOnEditFAQ(data));
      yield fetchFAQList({
        payload: { channelId: payload.faqData.channelId, requestedPage: 1 },
      });

      toast.success(getNotifMsg('CHANGES_SAVED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setIsSubmittingFAQForm(false);
  }
}

// Questions
export function* fetchQuestions({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const faqId = payload;
    yield handleFetchQuestions(faqId, locale);
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* addQuestion({ payload }) {
  const { questionData, setIsSubmittingForm } = payload;
  const { tempId, ...data } = questionData;
  const { locale } = yield select(mapState);
  try {
    const responseData = yield handleAddQuestion(data, locale);

    if (responseData.status === 'success') {
      yield put(
        setQuestionData({ ...createQuestionObject(responseData.data), tempId })
      );
      toast.success(getNotifMsg('QUESTION_SUCCESSFUL_ADDED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setIsSubmittingForm(false);
  }
}

export function* sortQuestions({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const { oldQuestions, newQuestionsByUpdatedOrders, faqId } = payload;

    // set new ordered questions list
    yield put(setQuestionsList(newQuestionsByUpdatedOrders));
    const newSortedQuestionIds = getQuestionIds(newQuestionsByUpdatedOrders);
    const responseData = yield handleSortQuestions(
      faqId,
      newSortedQuestionIds,
      locale
    );

    if (responseData.status === 'success') {
      toast.success(getNotifMsg('SORT_REORDER_SUCCESSFUL', locale));
      return;
    }

    toast.error(getNotifMsg('PROBLEM_REORDER', locale));
    // revert back to previous order if re-order is not successfull
    yield put(setQuestionsList(oldQuestions));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* updateQuestion({ payload }) {
  const { setIsSubmittingForm, data: questionData } = payload;
  const { tempId } = questionData;
  const { locale } = yield select(mapState);

  try {
    const { faqId, questionId, ...data } = payload.data;
    const responseData = yield handleUpdateQuestion(
      questionId,
      faqId,
      data,
      locale
    );

    if (responseData.status === 'success') {
      yield put(setQuestionData({ isExpanded: false, tempId }));
      toast.success(getNotifMsg('SUCCESSFULL_EDITING', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setIsSubmittingForm(false);
  }
}

export function* deleteQuestion({ payload }) {
  const {
    questionId,
    faqId,
    tempId,
    setConfirmModalVisible,
    setLoadingDelete = () => {},
  } = payload;
  const { locale } = yield select(mapState);

  try {
    const { questionsList } = yield select(mapState);
    const filteredQuestions = getFilteredQuestionList(tempId, questionsList);
    // if question is not created in server yet then do not proceed
    if (!questionId) {
      yield put(setQuestionsList(filteredQuestions));
      return;
    }
    // yield setLoadingDelete(true);
    const responseData = yield handleDeleteQuestion(questionId, faqId, locale);
    if (responseData.status === 'success') {
      toast.success(getNotifMsg('QUESTION_REMOVED', locale));
      yield setConfirmModalVisible(false);
      yield put(setQuestionsList(filteredQuestions));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield setLoadingDelete(false);
  }
}

export function* deleteFAQ({ payload }) {
  const {
    faqId,
    setLoadingDelete,
  } = payload;
  const { locale } = yield select(mapState);

  try {
    yield setLoadingDelete(true);
    yield handleDeleteFAQ(faqId, locale);

    yield put(deleteFAQById(faqId));
    toast.success(getNotifMsg("QUESTION_REMOVED", locale))

  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield setLoadingDelete(false);
  }
}

// FAQ
export function* onFetchFAQList() {
  yield takeLatest(faqTypes.FETCH_FAQ_LIST_START, fetchFAQList);
}

export function* onFetchMoreFAQList() {
  yield takeLatest(faqTypes.FETCH_MORE_FAQ_LIST, fetchMoreFAQList);
}

export function* onFetchSingleFAQ() {
  yield takeLatest(faqTypes.FETCH_SINGLE_FAQ_START, fetchSingleFAQ);
}

export function* onAddNewFAQ() {
  yield takeLatest(faqTypes.ADD_NEW_FAQ_START, addNewFAQ);
}

export function* onUpdateFAQ() {
  yield takeLatest(faqTypes.UPDATE_FAQ_START, updateFAQ);
}

export function* onUpdateFAQEnable() {
  yield takeLatest(faqTypes.UPDATE_FAQ_ENABLE_START, updateFAQEnable);
}

// Questions
export function* onFetchQuestions() {
  yield takeLatest(faqTypes.FETCH_QUESTION_START, fetchQuestions);
}

export function* onAddQuestion() {
  yield takeLatest(faqTypes.ADD_NEW_QUESTION_START, addQuestion);
}

export function* onSortQuestions() {
  yield takeLatest(faqTypes.SORT_QUESTIONS_START, sortQuestions);
}

export function* onUpdateQuestion() {
  yield takeLatest(faqTypes.UPDATE_QUESTION_START, updateQuestion);
}

export function* onDeleteQuestion() {
  yield takeLatest(faqTypes.DELETE_QUESTION_START, deleteQuestion);
}

export function* onDeleteFAQ() {
  yield takeLatest(faqTypes.DELETE_FAQ_START, deleteFAQ);
}

export default function* faqSagas() {
  yield all([
    call(onFetchFAQList),
    call(onFetchMoreFAQList),
    call(onFetchSingleFAQ),
    call(onAddNewFAQ),
    call(onUpdateFAQ),
    call(onFetchQuestions),
    call(onAddQuestion),
    call(onSortQuestions),
    call(onUpdateQuestion),
    call(onDeleteQuestion),
    call(onDeleteFAQ),
    call(onUpdateFAQEnable),
  ]);
}
